import { useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import { useAppContext } from 'contexts/AppContext';
import { useRouter } from 'next/router';
import { getContentCategories, getAllVideos, getTrendingVideos, getStaticBanners } from '../api';
import { track, scrollTo, cross, capitalize, svgLogoNew, useScrollPosition, useOnScreen, filtersIcon, timeSleep, sliceIfNecessary, useFirstRender } from 'utils';
import {  E_FILTER_CLEARED, E_FILTER_SELECTED } from 'utils/constants';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Pagination, Navigation, Autoplay } from 'swiper';
import "swiper/css";
import "swiper/css/pagination"
import "swiper/css/navigation"
import Link from 'next/link';
import Head from 'next/head';
import Nav from '@/components/modules/Nav/Nav';
import Button from '@/components/elements/Button/Button';
import AnimatedVideoThumbnail from '@/components/elements/AnimatedVideoThumbnail/AnimatedVideoThumbnail';
import XoLoader from '@/components/elements/XoLoader/XoLoader';
import ShowMore from '@/components/elements/ShowMore/ShowMore';
import LineLoader from '@/components/elements/LineLoader/LineLoader';
import FullScreenLoader from '@/components/modules/FullScreenLoader/FullScreenLoader';
import styles from '../styles/Watch.module.scss';
import { useWindowSize } from 'utils/index';

SwiperCore.use([Pagination, Navigation, Autoplay]);

const mainCategoriesOrder = [
    'porn',
    'eduporn',
    'guided sex',
    'behind the scenes',
];

const contentCategoriesOrder = {
    sexuality: [
        "heterosexual",
        "lesbian",
        "bisexual",
        "gay",
        "queer"
    ],
    party_size: [
        "threesome",
        "couple",
        "group sex"
    ],
    act: [
        "orgasm",
        "double penetration",
        "squirting"
    ],
    tone: [
        "realistic",
        "rough"
    ],
}


const Watch = ({ videosStripes, videosStripesB, contentCategories, staticBanners, allVideos, mainCategories }) => {

    contentCategories = useMemo(() => {
        const orderedContentCategories = {}
        Object.keys(contentCategoriesOrder).forEach(key => {
            orderedContentCategories[key] = [
                ...contentCategoriesOrder[key],
                ...contentCategories[key]?.filter((tag) => !contentCategoriesOrder[key].includes(tag)),
                ...contentCategories[key]?.filter((tag) => !contentCategories[key].includes(tag))
            ]
        })
        return orderedContentCategories
    }, [contentCategories])

    const {
        favorites,
        isMobile,
        setAppLoading,
        setDynamicRightMenu,
        appLoading,
        userStatus,
        isTapHoldExperiment,
        variantTapHold,
        filteredVideos,
        setFilteredVideos,
        activeFilters,
        setActiveFilters,
        searchInputValue,
        setSearchInputValue,
        isSearchEnabled,
        setIsSearchEnabled,
        lastSubmittedSearch,
        setLastSubmittedSearch,
        handleClickMainCategory,
        activeMainCategory,
        setActiveMainCategory,
        submittedSearchValue,
        searchVideosContextFunction
    } = useAppContext();

    const isFirstRender = useFirstRender();
    const router = useRouter();
    
    const mainSectionRef = useRef()
    const FiltersSectionWProps = (
        <FiltersSection
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
            activeMainCategory={activeMainCategory}
            setActiveMainCategory={setActiveMainCategory}
            contentCategories={contentCategories}
            mainCategories={mainCategories}
            mainSectionRef={mainSectionRef}
            appLoading={appLoading}
            setAppLoading={setAppLoading}
        />
    )
    const [videosStripesToUse, setVideosStripesToUse] = useState(null)
    const variantStripes = useRef(null)

    useEffect(()=>{
        const { tags, type, q } = router.query
        if (tags) {
            const tagsArr = tags.split(',')
            setActiveFilters(tagsArr)
        } else {
            setActiveFilters([])
        }
        if (type) {
            setActiveMainCategory(type)
        } else {
            setActiveMainCategory('')
        }
        if (q){
            setIsSearchEnabled(true)
            searchVideosContextFunction(q)
        } else {
            setIsSearchEnabled(false)
            setSearchInputValue('')
        }
    },[router?.query])

    useEffect(()=>{
        if(userStatus.id && isTapHoldExperiment){
            track("Tap hold experiment", {variant: variantTapHold})
        }
    }, [userStatus, variantTapHold, isTapHoldExperiment])

    useEffect(()=>{
        if(userStatus.id && !variantStripes.current){
            variantStripes.current = userStatus.id % 2 === 0 ? 0 : 1
            setVideosStripesToUse(variantStripes.current === 0 ? videosStripes : videosStripesB)
            track("Variant Stripes displayed 2.0", {variant: variantStripes.current})
        }
    }, [userStatus, variantStripes.current])

    useEffect(() => {
        setDynamicRightMenu(true)
        return () => setDynamicRightMenu(false)
    }, [])

    useEffect(()=>{
        async function handleFilteredVideos(){
            if(!isFirstRender || window.location.href.includes("/watch")){
                if(isFiltering()){
                    scrollTo(mainSectionRef, -64)
                    const filteredVideos = allVideos.filter(video => {
                        if(activeMainCategory !== ""){
                            if(video.content_type !== activeMainCategory.toLocaleLowerCase()) return false
                        }
                        if(activeFilters.length > 0){
                            if(!video.tags.some(tag => activeFilters.includes(tag))) return false
                        }
                        return true
                    })
                    const uniqueFilteredVideos = filteredVideos.filter((video, index, self) => self.findIndex(v => v.slug === video.slug) === index)
                    setFilteredVideos(filteredVideos)
                }else{
                    if(activeFilters.length === 0){
                        setFilteredVideos([])
                    }
                }
            }
        }
        handleFilteredVideos()
    }, [activeFilters, activeMainCategory])


    useEffect(()=>{
        setDynamicRightMenu({icon: filtersIcon(), markup:FiltersSectionWProps})
    }, [activeFilters, contentCategories, activeMainCategory])

    function isFiltering(){
        return (activeFilters.length > 0 || activeMainCategory !== "" || isSearchEnabled === true)
    }

    if(videosStripesToUse === null) return <FullScreenLoader />

    const trailer = staticBanners.find(banner => banner.content_type === activeMainCategory?.toLocaleLowerCase()) || staticBanners[0]

    return (
        <>
            <Head>
                <title>Watch | afterglow</title>
            </Head>
            <div className={`${styles.watch} watchpage`}>
                <div>
                    <HeroTrailer trailer={trailer} showText={true} key={trailer?.id}/>
                </div>
                <main className={styles.mainSection} ref={mainSectionRef}>
                    {!isMobile ?
                    FiltersSectionWProps
                    :
                    <></>
                    }
                    <section className={styles.videosSection}>
                        <div className={styles.activeFilters} >
                            {isFiltering() &&
                            <motion.div
                                className={styles.activeFilters__container}
                                initial={{height: 0}}
                                animate={{height: "auto"}}
                                exit={{height: 0}}
                            >
                                <AnimatePresence>
                                {activeMainCategory !== "" &&
                                    <motion.span
                                        className={styles.activeFilters__mainCategoryTag}
                                        initial={{opacity: 0, scale: .8}}
                                        animate={{opacity: 1, scale: 1}}
                                        exit={{opacity: 0, scale: .8}}
                                        transition={{duration: .2}}
                                    >
                                        <Tag
                                            tag={activeMainCategory}
                                            active={true}
                                            type="main category"
                                            onClick={()=>{
                                                const activeFiltersString = activeFilters.length ? `tags=${encodeURIComponent(activeFilters.join(','))}` : '';
                                                router.push(`/watch?${activeFiltersString}`, undefined, { shallow: true })
                                            }}
                                        />
                                    </motion.span>
                                }
                                </AnimatePresence>

                                <AnimatePresence>
                                    {activeFilters?.map((tag)=>
                                        <motion.span
                                            initial={{opacity: 0, scale: .8}}
                                            animate={{opacity: 1, scale: 1}}
                                            exit={{opacity: 0, scale: .8}}
                                            transition={{duration: .2}}
                                            key={tag}
                                        >
                                            <Tag
                                                tag={tag}
                                                active={true}
                                                onClick={()=>{
                                                    const newActiveFilters = activeFilters.filter(activeFilter => activeFilter !== tag)
                                                    const newActiveFiltersString = newActiveFilters.join(',')
                                                    const newActiveFiltersUrlString = newActiveFilters.length ? `tags=${encodeURIComponent(newActiveFiltersString)}` : ''

                                                    let mainCategoriesString = activeMainCategory ? `type=${encodeURIComponent(activeMainCategory)}` : ''

                                                    if (newActiveFilters.length && activeMainCategory) {
                                                        mainCategoriesString = `&${mainCategoriesString}`
                                                    }
                                                    router.push(`/watch?${newActiveFiltersUrlString}${mainCategoriesString}`, undefined, { shallow: true})
                                                }}
                                            />
                                        </motion.span>
                                    )}
                                </AnimatePresence>

                                {(isSearchEnabled && lastSubmittedSearch) ? (
                                    <div
                                        className={styles['searchEntry__Filter_Tag']}
                                        onClick={()=>{
                                            router.push('/watch', undefined, { shallow: true })
                                        }}
                                    >
                                        {`${capitalize(lastSubmittedSearch)}`}
                                        <span>{cross(11, "white")}</span>
                                    </div>
                                ) : null}

                                <motion.div
                                    className={styles.clearAll}
                                    onClick={()=>{
                                        router.push('/watch', undefined, { shallow: true })
                                        track(E_FILTER_CLEARED, {}, true)
                                    }}
                                    initial={{opacity: 0}}
                                    animate={{opacity: 1}}
                                    transition={{delay: .15}}
                                >
                                    Clear All
                                    <span>{cross(11)}</span>
                                </motion.div>
                            </motion.div>
                            }
                        </div>
                        {appLoading &&
                            <div className={`${styles.loadingAnimationContainerForSearch}`}>
                                <div className={`${styles.xoLoaderContainerForSearch}`}>
                                    <XoLoader/>
                                </div>
                            </div>
                        }
                        
                        
                        {isFiltering() && !appLoading ?
                        <div className={`${styles.videosSection__videos} ${styles.videosSection__filteredVideos} ${filteredVideos?.length < 1 ? styles["videosSection__filteredVideos-noResults"] : ""}`}>
                            {filteredVideos?.length > 0 ?
                            filteredVideos.map((video, i)=>
                                <ScalableVideoPreview video={video} key={video.title + i} />
                            )
                            :
                            <div className={styles.videosSection__noResults}>
                                {!appLoading && searchInputValue &&
                                    <motion.div initial={{scale: .95, opacity: 0}} animate={{scale: 1, opacity: .3}}>
                                        Oops! Looks like none of our video titles, tags, or descriptions contain the term “{submittedSearchValue}.” Try narrowing your preferences using the filters in the sidebar.
                                    </motion.div>
                                }
                                {!appLoading && !searchInputValue &&
                                    <motion.div initial={{scale: .95, opacity: 0}} animate={{scale: 1, opacity: .3}}>
                                        Search for videos by title, tags, or description.
                                    </motion.div>
                                }
                            </div>
                            }
                        </div>
                        :
                        <div className={styles.videosSection__videos}>
                            {favorites.length > 0 &&
                                <motion.div
                                style={{overflow: "hidden"}}
                                initial={{opacity: 0, height: 0}}
                                animate={{opacity: 1, height: "auto", transition:{stiffness: 0}}}
                                >
                                    <VideosStripe title="Favorites" videos={favorites} key={"Favorites " + favorites.length} />
                                </motion.div>
                            }
                            {Object.keys(videosStripesToUse).map((stripe)=>
                                <VideosStripe title={stripe} videos={videosStripesToUse[stripe]} key={stripe} />
                            )}
                        </div>
                        }
                    </section>
                </main>
            </div>
        </>
     );
}


export const MainCategories = ({categories, activeMainCategory, setActiveMainCategory }) => {

    const { navHeight, isMobile, setOpenDynamicRightMenu, handleClickMainCategory } = useAppContext();

    const NAV_HEIGHT = isMobile ? 64 : navHeight.desktop;
    function getCategoriesPaddingTop(){
        return NAV_HEIGHT < 100 ? NAV_HEIGHT : 64
    }

    const filteredMainCategories = categories.filter((category)=> category !== activeMainCategory)

    return (
        <div className={styles.mainCategories} data-class="MainCategories">
            <div className={styles.mainCategories__container}>
                <div className={styles.mainCategories__category_header_text}>
                    Category
                </div>
                {filteredMainCategories.map((category)=>
                    <div className={`${styles.mainCategories__card} ${activeMainCategory === category ? styles['mainCategories__card-active'] : ""}`} key={category}>
                        <Button theme="transparent" onClick={(e)=>handleClickMainCategory(category)}>{category}</Button>
                    </div>
                )}
            </div>
        </div>
    )
}

const FiltersSection = ({
    activeFilters,
    setActiveFilters,
    contentCategories,
    mainCategories,
    activeMainCategory,
    setActiveMainCategory,
    mainSectionRef,
    setAppLoading={setAppLoading}
}) => {

    const {
        isMobile,
        appLoading,
        setOpenDynamicRightMenu,
        searchInputValue,
        setSearchInputValue,
    } = useAppContext();

    const router = useRouter();

    const scrollPosition = useScrollPosition();
    const elementRef = useRef();
    const isFirstRender = useFirstRender();
    const [hasScrolled, setHasScrolled] = useState(false)

    useEffect(()=>{
        if(!isFirstRender && !hasScrolled){
            setHasScrolled(true)
        }
    }, [scrollPosition])

    return(<section className={styles.filtersSection} ref={elementRef}>
            <div className={`${styles.filtersSection__fixable} ${(isMobile)?styles['filtersSection__fixable-fixed']:null}`}>
            {isMobile &&
                <div className={styles.filtersSection__mobileHeader}>
                    <Link href="/" passHref>
                        <div className={styles.nav__hamburgerMenuLogo}>
                            {svgLogoNew()}
                        </div>
                    </Link>

                    <span className={styles.filtersSection__appLoader}>
                    {appLoading &&
                        <LineLoader />
                    }
                    </span>

                    <div className={styles.filtersSection__browse}>
                        <div className={styles['filters_search_container']}>
                            <form
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    if (!searchInputValue) return
                                    setAppLoading(true)
                                    setOpenDynamicRightMenu(false);
                                    router.push(`/watch?q=${encodeURIComponent(searchInputValue)}`, undefined, { shallow: true })
                                    track("Search Videos Mobile", { search_term: searchInputValue })
                                }}
                            >
                                <input
                                    className={styles['filters_search_input_field']}
                                    type="text"
                                    id="filters_search_input_id"
                                    placeholder="Search for videos"
                                    value={searchInputValue}
                                    onChange={(e)=>{
                                        setSearchInputValue(e.target.value);
                                    }}
                                    autoComplete="off"
                                />
                            </form>
                        </div>
                    </div>
                </div>
            }
            {!isMobile &&
                <form onSubmit=
                    {(event) =>{
                        event.preventDefault();
                        if (!searchInputValue) return
                        setAppLoading(true)
                        router.push(`/watch?q=${encodeURIComponent(searchInputValue)}`, undefined, { shallow: true })
                        track("Search Videos Desktop", { search_term: searchInputValue })
                    }}
                >
                    <div className={styles['filters_search_container_desktop']}>
                        <input
                            className={styles['filters_search_input_field_desktop']}
                            type="text" id="filters_search_input_id_desktop"
                            placeholder="Search for videos"
                            value={searchInputValue}
                            onChange={(e)=>{
                                setSearchInputValue(e.target.value)
                            }}
                            autoComplete="off"
                        />
                        <button className={styles['search_input_area_button']}>
                            <img className={styles['search_input_area_icon_desktop']} src="/next/Search_Icon_White.png" />
                        </button>
                    </div>
                </form>
            }
            <MainCategories
                categories={mainCategories}
                activeMainCategory={activeMainCategory}
                setActiveMainCategory={setActiveMainCategory}
            />
            {contentCategories && Object.keys(contentCategories).map((type)=>
                <TagTypes type={type} activeFilters={activeFilters} setActiveFilters={setActiveFilters} key={type} contentCategories={contentCategories} />
            )}
            </div>
        </section>)
}

export const HeroTrailer = ({trailer, showText=true}) => {
    const { isMobile } = useAppContext();


    return (
        <div
        onClick={()=>track("Watch Page Banner Clicked", {banner_text: trailer?.title, banner_image: trailer?.image})}
        >
        <Link href={trailer?.href || ""}>
            <a>
            <section className={`${styles.heroTrailer} ${styles['heroTrailer-' + trailer?.content_type]}`}
            style={{
                backgroundImage: `url(${isMobile? trailer.mobile_image : trailer.image})`
            }}
            data-class="HeroTrailer"
            >
                <div className={styles.heroTrailer__info}
                style={{opacity: showText ? 1 : 0}}
                >
                    <div className={styles.heroTrailer__texts}>
                        <h1>{trailer?.title}</h1>
                        {trailer?.description &&
                            <p>{trailer?.description}</p>
                        }
                    </div>
                    <Link href={trailer?.href || ""} passHref>
                        <span>
                        </span>
                    </Link>
                </div>

                <div className={styles.heroTrailer__bottomFade} />
            </section>
            </a>
        </Link>
        </div>
    )
}

export const HeroTrailerSlider = ({trailers, showText=true}) => {
    return (
        <Swiper
        pagination={true}
        slidesPerView={'auto'}
        centeredSlides={true}
        speed={600}
        className="mySwiper heroSwiper heroTrailerSlider">
            {trailers.map((trailer, i)=>
                <SwiperSlide key={i}>
                    <HeroTrailer trailer={trailer} showText={showText} />
                </SwiperSlide>
            )}
        </Swiper>
    )
}

const TagTypes = ({type, activeFilters, setActiveFilters, contentCategories}) => {
    const {
        isMobile,
        setOpenDynamicRightMenu,
        setIsSearchEnabled,
        setSearchInputValue,
        searchInputValue,
        activeMainCategory,
    } = useAppContext()

    const router = useRouter();

    const buttonText = {
        more: "",
        less: "",
    }

    function handleTagClick(tag){
        isMobile && setOpenDynamicRightMenu(false)
        const newActiveFilters = [ ...activeFilters, tag ]
        const newActiveFiltersString = newActiveFilters.join(',')
        const newActiveFiltersUrlString = `tags=${encodeURIComponent(newActiveFiltersString)}`
        const mainCategoriesString = activeMainCategory ? `&type=${encodeURIComponent(activeMainCategory)}` : ''
        router.push(`/watch?${newActiveFiltersUrlString}${mainCategoriesString}`, undefined, { shallow: true})
    }

    return (
        <div className={`${styles.tagsType}`}>
            <div className={styles.tagsType__titleSection}>
                <div className={styles.tagsType__title}>{`${(type.replace('_',' '))}`}</div>
            </div>
            <div>
                <div className={`${styles.tagsType__container} ${styles["tagsType__container-wShowMore"]}`}>
                    <ShowMore inlineButton={true} buttonAlign="right" buttonText={buttonText} color="black">
                        {contentCategories[type].filter((tag)=> !activeFilters.includes(tag))?.map((tag)=>
                            <Tag tag={tag} type={type} onClick={()=>handleTagClick(tag)} key={tag} />
                        )}
                    </ShowMore>
                </div>
            </div>
        </div>
    )
}

export const Tag = ({tag, type, onClick, active=false}) => {

    function handleTagClick(){
        onClick()
        if(!active){
            track(E_FILTER_SELECTED, {
                filter_type: type,
                filter_name: tag,
            }, true)
        }
    }

    return (
        <div className={`${styles.tag}  ${active ? styles["tag-active"] : ""}`} data-type="tag" onClick={handleTagClick}>
            {capitalize(tag)}
            {active &&
                <span>{cross(11, "white")}</span>
            }
        </div>
    )
}

export const ScalableVideoPreview = ({video, index, setContextHovering=false}) => {
    const { isMobile } = useAppContext();
    const [hovering, setHovering] = useState(false)
    const DELAY = 350;
    const MAX_TITLE_CHARACTERS = isMobile ? 40 : 60
    const [animateTitle, setAnimateTitle] = useState(false)
    const hoveringRef = useRef()

    useEffect( ()=>{
        async function handleHover(){
            if(!isMobile){
                (setContextHovering && hovering) && setContextHovering(index)
                hoveringRef.current = hovering
                hovering && await timeSleep(DELAY)
                if(!(!hoveringRef.current && hovering)){
                    setAnimateTitle(hovering)
                }
            }
        }
        handleHover()
    }, [hovering])

    return (
        <Link href={`/video/${video.slug}`} style={{zIndex: hovering ? 3 : 1}} passHref>
        <a style={hovering ? {zIndex: 3} : {}}>
            <motion.div className={`${styles.videosStripe__video} ${hovering ? styles["videosStripe__video-hovering"] : ""}`} initial={{scale: .95, opacity: 0}} animate={{scale: 1, opacity: 1}}>
                <motion.div
                className={styles.videosStripe__thumbnailWrapper}
                whileHover={!isMobile && {scale:1.25, z:3, transition: { duration: .3, delay: DELAY/1000}}}
                onAnimationStart={()=>setHovering(true)}
                onMouseLeave={()=>setHovering(false)}
                onMouseEnter={()=>setHovering(true)}
                onTouchStart={()=>setHovering(true)}
                onTouchEnd={()=>setHovering(false)}
                >
                    <AnimatedVideoThumbnail video={video} delay={DELAY} />
                </motion.div>
                <h3 className={`${styles.videosStripe__videoTitle} ${animateTitle ? styles['videosStripe__videoTitle-hovering']: ''}`}>{sliceIfNecessary(video.title, MAX_TITLE_CHARACTERS)}</h3>
            </motion.div>
        </a>
        </Link>
    )
}

export const VideosStripe = ({title, videos}) => {

    const VIDEOS_SET =  videos.length >= 10 ? Math.ceil(videos?.length / 2) : videos.length;
    const { isMobile } = useAppContext();
    const [videosToShow, setVideosToShow] = useState(videos?.slice(0,VIDEOS_SET))
    const [hovering, setHovering] = useState();
    const moreVideosToRender = videosToShow.length < videos.length;
    const loaderRef = useRef();
    const isIntersecting = useOnScreen(loaderRef)

    useEffect(()=>{
        setVideosToShow(videos?.slice(0,VIDEOS_SET))
    }, [videos])

    useEffect(()=>{
        async function handleIntersect(){
            if(isIntersecting && videosToShow.length < videos.length){
                await timeSleep(500)
                setVideosToShow([...videosToShow, ...videos?.slice(videosToShow.length, videosToShow.length + VIDEOS_SET)])
            }
        }
        handleIntersect()
    }, [isIntersecting, videos])

    return (
        <section className={`${styles.videosStripe} videosStripe`}>
            <div className={styles.videosStripe__titleSection}>
                <h2>{title}</h2>
            </div>
            <div>
            <Swiper
                className="mySwiper"
                slidesPerView={'auto'}
                spaceBetween={isMobile ? 9 : 25}
                centeredSlides={false}
                navigation={true}
            >
              {videosToShow.map((video, i)=>
                  <SwiperSlide key={video.title} style={{zIndex: hovering === i ? 3 : 0}}>
                      <ScalableVideoPreview video={video} index={i} setContextHovering={setHovering} />
                  </SwiperSlide>
              )}

              {moreVideosToRender &&
              <SwiperSlide key={videosToShow.length}>
                <div className={styles.videosStripe__loader}>
                    <span ref={loaderRef}>
                        <XoLoader size={isMobile ? 20 : 30} />
                    </span>
                </div>
              </SwiperSlide>
              }
            </Swiper>
            </div>
        </section>
    )
}

export function buildVideosStripes(videos, stripes){

    const videosStripes = {}

    stripes.forEach((stripe)=>{
        const videosInStripe = videos.filter((video)=>{
            if(stripe.type === 'content_category') return video.tags.includes(stripe.value)
            return video.content_type === stripe.value
        })
        videosStripes[stripe.label] = videosInStripe
    })

    return videosStripes
}

const stripes = [
    {
        type: "content_category",
        value: "threesome",
        label: "Threesome"
    },
    {
        type: "content_category",
        value: "orgasm",
        label: "Orgasm"
    },
    {
        type: "content_category",
        value: "lesbian",
        label: "Lesbian"
    },
    {
        type: "content_category",
        value: "rough",
        label: "Rough"
    },
]

const stripesB = [
    {
        type: "content_category",
        value: "heterosexual",
        label: "Heterosexual"
    },
    {
        type: "content_category",
        value: "threesome",
        label: "Threesome"
    },
    {
        type: "content_category",
        value: "lesbian",
        label: "Lesbian"
    },
    {
        type: "content_category",
        value: "group sex",
        label: "Group sex"
    },
]

const DEFAULT_STATIC_BANNER = [
    {
        id: 1,
        href: "https://xoafterglow.com/video/lip-service",
        showText: true,
        image: "https://d2mbipel1ly09j.cloudfront.net/thumbs/static_banner_img_6%20Aquaphilia_wide.png",
        mobile_image: "https://d2mbipel1ly09j.cloudfront.net/thumbs/static_banner_img_porn-m.png",
        content_type: "porn",
        title: "Everything from soft and slow sex acts, to gang bangs & BDSM",
        description: null
    }
]

export async function getStaticProps( ) {
    const [trendingRes, contentCategoriesRes, staticBannersRes] = await Promise.allSettled([getTrendingVideos(), getContentCategories(), getStaticBanners()])
    const allVideos = []
    for(let i in [0,1,2]){
        const from = i*100
        const to = from + 100
        const thisVideos = await getAllVideos({from, to})
        allVideos.push(...thisVideos.data.videos)
    }
    const uniqueAllVideos = allVideos.filter((video, index, self) => self.findIndex(v => v.slug === video.slug) === index)
    const newReleases = uniqueAllVideos.slice(0,25)
    const trending = trendingRes.value.data.videos;
    const contentCategories = contentCategoriesRes.value.data.contentCategories

    const staticBanners = staticBannersRes.value.data?.map((trailer)=>{
        return {
            ...trailer,
            image: encodeURI(trailer.image),
            mobile_image: encodeURI(trailer.mobile_image),
        }
    }) || DEFAULT_STATIC_BANNER


    const mainCategories = contentCategoriesRes.value.data.mainCategories
    const orderedMainCategories = [
        ...mainCategoriesOrder,
        ...mainCategories.filter((category) => !mainCategoriesOrder.includes(category)),
    ]

    let videosStripes = buildVideosStripes(uniqueAllVideos, stripes)
    videosStripes = {"New Releases": newReleases, "Trending": trending, ...videosStripes}
    const videosStripesB = {"New Releases": newReleases, "Trending": trending, ...buildVideosStripes(uniqueAllVideos, stripesB)}

    const orderedContentCategories = {}
    Object.keys(contentCategoriesOrder).forEach(key => {
        orderedContentCategories[key] = [
            ...contentCategoriesOrder[key],
            ...contentCategories[key].filter((tag) => !contentCategoriesOrder[key].includes(tag))
        ]
    })

    return {
        props:{
            videosStripes,
            videosStripesB,
            contentCategories: orderedContentCategories,
            allVideos: uniqueAllVideos,
            mainCategories: orderedMainCategories,
            staticBanners,
        },
        revalidate: 60 * 60
    }
}

Watch.Nav = <Nav dark={true} />
Watch.Footer = false
Watch.authenticateClientSide = true

export { contentCategoriesOrder };
export default Watch;
